import React, {useRef} from 'react'

import {
  Wrapper,
  LabelRow,
  LabelEl,
  SelectWrapper,
  StyledSelect,
  IconSlot,
  HelperText,
  LabelIcon,
  LabelReq,
} from './Select.styles'

// ─────────────────────────────────────────────
// React 17-compatible unique id
// ─────────────────────────────────────────────

let idCounter = 0

function useUniqueId(prefix = 'select') {
  const idRef = useRef(null)

  if (idRef.current === null) {
    idRef.current = `${prefix}-${++idCounter}`
  }

  return idRef.current
}

// ─────────────────────────────────────────────
// Default Chevron Icon
// ─────────────────────────────────────────────

function ChevronDownIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 20 20" fill="none">
      <path
        d="M5 7.5L10 12.5L15 7.5"
        stroke="currentColor"
        strokeWidth="1.8"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  )
}

// ─────────────────────────────────────────────
// Component
// ─────────────────────────────────────────────

export function Select({
  label,

  labelLeftIcon,

  helperText,

  errorText,

  leftIcon,

  rightIcon,

  options = [],

  placeholder = 'Select option',

  size = 'sm',

  variant = 'default',

  disabled = false,

  required = false,

  id: externalId,

  ...nativeProps
}) {
  const autoId = useUniqueId('select')

  const selectId = externalId ?? autoId

  const helperId = `${selectId}-helper`

  const errorId = `${selectId}-error`

  const hasError = Boolean(errorText)

  const hasLeftIcon = Boolean(leftIcon)

  const hasRightIcon = Boolean(rightIcon)

  const hasLabelLeftIcon = Boolean(labelLeftIcon)

  return (
    <Wrapper>
      {label && (
        <LabelRow>
          {hasLabelLeftIcon && <LabelIcon>{labelLeftIcon}</LabelIcon>}

          <LabelEl htmlFor={selectId} variant={variant}>
            {label}
          </LabelEl>

          {required && (
            <LabelReq htmlFor={selectId} variant={variant}>
              *
            </LabelReq>
          )}
        </LabelRow>
      )}

      <SelectWrapper>
        {hasLeftIcon && (
          <IconSlot side="left" aria-hidden="true">
            {leftIcon}
          </IconSlot>
        )}

        <StyledSelect
          id={selectId}
          size={size}
          variant={variant}
          disabled={disabled}
          hasError={hasError}
          hasLeftIcon={hasLeftIcon}
          hasRightIcon={hasRightIcon}
          aria-invalid={hasError}
          aria-describedby={
            [errorText && errorId, helperText && helperId]
              .filter(Boolean)
              .join(' ') || undefined
          }
          {...nativeProps}
        >
          <option value="" disabled hidden>
            {placeholder}
          </option>

          {options.map(option => {
            if (typeof option === 'string') {
              return (
                <option key={option} value={option}>
                  {option}
                </option>
              )
            }

            return (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            )
          })}
        </StyledSelect>

        <IconSlot side="right" aria-hidden="true">
          {rightIcon || <ChevronDownIcon />}
        </IconSlot>
      </SelectWrapper>

      {hasError && (
        <HelperText id={errorId} isError role="alert">
          {errorText}
        </HelperText>
      )}

      {!hasError && helperText && (
        <HelperText id={helperId}>{helperText}</HelperText>
      )}
    </Wrapper>
  )
}
